import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export type SembonusUserInfo = {
  memberOptions: {
    countries: {
      countryCode: string;
      countryName: string;
    }[];
  };
  member: {
    userName: string;
    email: string;
    firstName: string;
    lastName: string;
    streetAddress: string | null;
    postalCode: string | null;
    city: string | null;
    countryCode: string | null;
    birthDate: string | null;
    canBeSembonusUser: boolean;
    travelAgencyId: string | null;
    isSembonusUser: boolean;
    // added by us, to protect sensitive user details in logging
    userNameHash: string;
    internalUser: boolean;
  };
};

export type ContextTypes = StateContextTypes & {
  checkLoginStatus: () => Promise<StateContextTypes>;
  updateUserInfo: (user: SembonusUserInfo) => void;
};

export type StateContextTypes = {
  isLoggedIn?: boolean;
  isCheckingLoggedInState: boolean;
  userInfo?: SembonusUserInfo;
};

export const UserContext = createContext<ContextTypes>({
  isLoggedIn: undefined,
  isCheckingLoggedInState: true,
  userInfo: undefined,
  checkLoginStatus: (): any => {
    /* noop */
  },
  updateUserInfo: (): void => {
    /* noop */
  },
});

export function useSembonusUser(): ContextTypes {
  return useContext(UserContext);
}

const hashValue = async (text: string) => {
  // Encode the username string into a Uint8Array, assuming UTF-8 encoding
  const encoder = new TextEncoder();
  const data = encoder.encode(text);

  // Hash the data with SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert the hash ArrayBuffer to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
};

export const UserProvider: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [loginState, setLoginState] = useState<StateContextTypes>({
    isCheckingLoggedInState: true,
    isLoggedIn: undefined,
    userInfo: undefined,
  });

  const checkLoginStatus = useCallback(async () => {
    const notLoggedInState: StateContextTypes = {
      isLoggedIn: false,
      isCheckingLoggedInState: false,
      userInfo: undefined,
    };

    try {
      const loggedInResponse = await fetch('/d/mvc/Members/GetMember', {
        headers: {
          accept: 'application/json',
        },
      });

      if (!loggedInResponse.ok) {
        setLoginState(notLoggedInState);
        return notLoggedInState;
      }

      const userInfo: SembonusUserInfo = await loggedInResponse.json();

      const userNameHash = await hashValue(userInfo.member.userName);
      const internalUser =
        /@sembo\.[a-z]+/.test(userInfo.member?.email) ||
        /@sembo\.[a-z]+/.test(userInfo.member.userName);

      const userState: StateContextTypes = {
        isCheckingLoggedInState: false,
        isLoggedIn: true,
        userInfo: {
          ...userInfo,
          member: {
            ...userInfo.member,
            userNameHash,
            internalUser,
          },
        },
      };

      setLoginState(userState);

      return userState;
    } catch (e) {
      setLoginState(notLoggedInState);
      return notLoggedInState;
    }
  }, []);

  const updateUserInfo = useCallback((user: SembonusUserInfo) => {
    setLoginState({
      ...loginState,
      isCheckingLoggedInState: false,
      isLoggedIn: true,
      userInfo: user,
    });
  }, []);

  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  return (
    <UserContext.Provider
      value={{ ...loginState, checkLoginStatus, updateUserInfo }}
    >
      {children}
    </UserContext.Provider>
  );
};
